import React, { useMemo, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, List, ListItemButton, ListItemText, TextField, Typography } from '@mui/material';

import { OrganizationPreset } from '@paragon/contract-library';
import { Dialog } from '@paragon/popup-state';
import { createScrollbarStyling } from '@paragon/themes';

export function ApplyPresetDialog<TSettings>(props: {
  presets: OrganizationPreset<TSettings>[];
  onApplyPreset(preset: OrganizationPreset<TSettings>): void;

  onClose(): void;
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPresets = useMemo(() => {
    return props.presets.filter((preset) => preset.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [props.presets, searchQuery]);

  return (
    <Dialog
      insistent
      title="Apply Preset"
      maxWidth="md"
      variant="design"
      onCancel={props.onClose}
      contentSx={{ display: 'flex', flexDirection: 'column', maxHeight: '60vh' }}
    >
      <TextField
        fullWidth
        autoFocus
        sx={{ marginTop: 1, minWidth: '300px' }}
        autoComplete="off"
        variant="outlined"
        label="Search"
        placeholder="Search for a preset..."
        value={searchQuery}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {searchQuery !== '' && (
                  <IconButton onClick={() => setSearchQuery('')}>
                    <ClearIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          },
        }}
        onChange={(event) => setSearchQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.nativeEvent.code === 'Enter') {
            setSearchQuery('');
            event.stopPropagation();
          }
        }}
      />

      {filteredPresets.length === 0 ? (
        <Typography variant="body1" sx={{ marginTop: 1, color: 'text.secondary' }}>
          No presets match your search query.
        </Typography>
      ) : (
        <List sx={[{ overflowY: 'auto' }, createScrollbarStyling]}>
          {filteredPresets.map((preset) => (
            <ListItemButton
              key={preset.guid}
              onClick={() => {
                props.onApplyPreset(preset);
              }}
            >
              <ListItemText primary={preset.name} />
            </ListItemButton>
          ))}
        </List>
      )}
    </Dialog>
  );
}
